/**
 * Pure display helpers for Song Intake (Music Provider upload oversight surface).
 * Status is derived from backend oversightBucket / attentionKind — not a client state machine.
 */

import type { MusicUploadReviewRow, OversightBucket } from "./musicUploadReviewsApi";

export const OVERSIGHT_VIEWS: Array<{
  key: OversightBucket;
  label: string;
}> = [
  { key: "all", label: "All Activity" },
  { key: "needs-attention", label: "Needs Attention" },
  { key: "flagged-security", label: "Flagged / Security" },
  { key: "blocked", label: "Blocked" },
  { key: "admitted", label: "Admitted / Released" },
];

export function oversightBadgeVariant(
  bucket: string | null | undefined,
): string {
  switch (bucket) {
    case "flagged-security":
      return "danger";
    case "blocked":
      return "dark";
    case "needs-attention":
      return "warning";
    case "admitted":
      return "success";
    default:
      return "info";
  }
}

export function processingJobPath(jobId: string | null | undefined): string | null {
  if (!jobId) return null;
  return `/processing-jobs/${encodeURIComponent(jobId)}`;
}

export function trackPath(trackId: string | null | undefined): string | null {
  if (!trackId) return null;
  return `/songs?trackId=${encodeURIComponent(trackId)}`;
}

export function primaryOversightAction(
  row: MusicUploadReviewRow,
):
  | { kind: "none"; label: string }
  | { kind: "open-job"; label: string; jobId: string }
  | { kind: "review-policy"; label: string; jobId: string }
  | { kind: "review-metadata"; label: string; jobId: string }
  | { kind: "security-details"; label: string } {
  if (row.oversightBucket === "flagged-security") {
    return { kind: "security-details", label: "View security evidence" };
  }
  const jobId = row.processingJobId || row.processingJob?.jobId || null;
  if (!jobId) {
    if (row.attentionKind === "deferred-zip") {
      return { kind: "none", label: "ZIP deferred — no Processing Job" };
    }
    return { kind: "none", label: "No Processing Job" };
  }
  if (row.attentionKind === "staging-auto-admit-gate") {
    return {
      kind: "open-job",
      label: "Open Processing Job",
      jobId,
    };
  }
  if (row.attentionKind === "policy-hold") {
    return { kind: "review-policy", label: "Review Policy", jobId };
  }
  if (
    row.attentionKind === "metadata-review" ||
    row.attentionKind === "catalog-link"
  ) {
    return { kind: "review-metadata", label: "Review / Resolve", jobId };
  }
  if (row.attentionKind === "technical-failure") {
    return { kind: "open-job", label: "Open Processing Job", jobId };
  }
  return { kind: "open-job", label: "Open Processing Job", jobId };
}
